<script setup>
import { computed } from 'vue'
import { icons } from '@/helpers/icons'

/**
 * BatchPreviewStack — a batch's preview cards as a fanned stack.
 *
 * Top card upright, the two behind it rotated in opposite directions; an
 * empty-batch placeholder when the batch has no previews. Extracted from
 * `AllBatchesPanel/BatchListItem.vue` when the Inventory recent-batch chips
 * needed the same illustration — a distinct visual role rendered identically in
 * two places is one component, not two copies of six `<img>` tags.
 *
 * Size comes from the caller's class (`w-9 h-12`, `w-[33px] h-10`, …) so the
 * stack fits both the panel row and the smaller chip without a size prop.
 */
const props = defineProps({
  /** `batch.preview_cards` — up to 3 are drawn. */
  cards: { type: Array, default: () => [] },
  /** Dim the stack (closed batches read as inactive in the panel). */
  dimmed: { type: Boolean, default: false },
})

const previewCards = computed(() => props.cards || [])
</script>

<template>
  <div class="relative shrink-0" :class="{ 'opacity-50': dimmed }">
    <template v-if="previewCards.length">
      <img
        v-if="previewCards[2]"
        :src="previewCards[2].card_front_image"
        class="absolute inset-0 h-full w-full object-cover rounded-[3px] border border-white shadow-sm -rotate-[21deg] scale-[0.88]"
      />
      <img
        v-if="previewCards[1]"
        :src="previewCards[1].card_front_image"
        class="absolute inset-0 h-full w-full object-cover rounded-[3px] border border-white shadow-sm -rotate-[9deg] scale-[0.98]"
      />
      <img
        v-if="previewCards[0]"
        :src="previewCards[0].card_front_image"
        class="absolute inset-0 h-full w-full object-cover rounded-[3px] border border-white shadow-sm"
        :class="previewCards.length > 1 ? 'rotate-[12deg]' : ''"
      />
    </template>
    <span v-else v-html="icons.empty_batch_preview" />
  </div>
</template>
